import React, { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Fournisseur, HistoriqueLogs, MethodePaiement, StatutEntreprise } from '../../../../../common/src/entities';
import ChampFormulaire from '../../Common/ChampFormulaire';
import DropdownEntiteFormulaire from '../../Common/DropdownEntiteFormulaire';
import { DateTime } from 'luxon';
import FormulaireModificationEntite from '../Utils/FormulaireModificationEntite';
import { schemaFournisseur } from '../../../../../common/src/schemas';
import { toISODate } from '../../../Utils/dateUtils';
import { useFormikContext } from 'formik';
import ChampsAssocies from '../Utils/ChampsAssociés';
import ChampMultipleLieu from '../Lieux/ChampMultipleLieu';
import ChampMultipleContact from '../Contacts/ChampMultipleContact';
import { Col, Row } from 'react-bootstrap';
import { useGetQuery } from '../../../Utils/api';
import LogsLoader, { dateFormatter } from '../Utils/Log';
import { EtatEntite } from '../../Common/EtatEntite';
import CheckboxFormulaireEtatEntite from '../../Common/CheckboxFormulaireEtatEntite';
import { TitreCarte } from '../../Common/TitreCarte';
import { LABEL_COL } from '../constantes';
import { MyPaper } from '../Utils/MyPaper';

export function ChampsFournisseur(): ReactElement {
    const {
        values: { activites },
    } = useFormikContext<Fournisseur>();

    const activite_combines = [
        ...(activites ?? []).map((a) => ({
            ...a,
            name: (
                <>
                    Fournisseur de <b>{a.name}</b>
                </>
            ),
        })),
    ];

    function EtatFournisseur(): JSX.Element {
        return (
            <EtatEntite>
                <CheckboxFormulaireEtatEntite id="actif" titre="Actif" />
                <CheckboxFormulaireEtatEntite id="b_entente" titre="Entente" />
            </EtatEntite>
        );
    }

    return (
        <div className="pb-5">
            {EtatFournisseur()}
            <MyPaper>
                <ChampFormulaire labelCol={LABEL_COL} id="name" titre="Nom" estPetit />
                <ChampMultipleContact
                    labelCol={LABEL_COL}
                    id="contact_par_defaut"
                    maxSize={1}
                    title="Contact par défaut"
                    avecUnBouton
                />
                <ChampFormulaire
                    labelCol={LABEL_COL}
                    type="date"
                    id="trigger_suivi"
                    titre="Date de suivi"
                    parser={(date) => toISODate(date as string)}
                    estPetit
                />
                <ChampFormulaire labelCol={LABEL_COL} id="notes" titre="Notes internes" textarea />
                <ChampFormulaire
                    labelCol={LABEL_COL}
                    type="number"
                    id="pct_commission_defaut"
                    titre="Commision par defaut (%)"
                    estPetit
                />
                <ChampMultipleLieu id="lieu" maxSize={1} title="Lieu" labelCol={LABEL_COL} avecUnBouton />
            </MyPaper>
            <MyPaper>
                <DropdownEntiteFormulaire
                    labelCol={LABEL_COL}
                    id="fk_status_entreprise"
                    titre="Statut"
                    entite={StatutEntreprise}
                    champs="status_entreprise"
                    estPetit
                />
                <ChampFormulaire labelCol={LABEL_COL} id="neq" titre="NEQ" estPetit />
                <ChampFormulaire labelCol={LABEL_COL} id="tps" titre="# TPS" estPetit />
                <ChampFormulaire labelCol={LABEL_COL} id="tvq" titre="# TVQ" estPetit />
                <ChampMultipleContact
                    labelCol={LABEL_COL}
                    id="contact_gestionnaire"
                    maxSize={1}
                    title="Gestionnaire"
                    avecUnBouton
                />
                <ChampFormulaire
                    type="date"
                    id="debut_relation_date"
                    titre="Date de debut de la relation"
                    parser={(date) => toISODate(date as string)}
                    labelCol={LABEL_COL}
                    estPetit
                />
                <Row>
                    <Col xs={LABEL_COL}>Delai de reception de facture</Col>
                    <Col>
                        <ChampFormulaire
                            type="number"
                            id="delais_reception_facture"
                            titre="Jour(s)"
                            placeholder="Jours(s)"
                            cacherTitre
                            estPetit
                        />
                    </Col>
                    <Col>
                        <ChampFormulaire
                            id="delais_reception_facture_texte"
                            titre="Avis"
                            placeholder="Avis"
                            cacherTitre
                            estPetit
                        />
                    </Col>
                </Row>
                <ChampsAssocies
                    labelCol={LABEL_COL}
                    items={activite_combines}
                    url="activites"
                    titre="Activités associées"
                    montrerActifInactif
                />
            </MyPaper>
            <TitreCarte>Coordonnées bancaires et information de paiement</TitreCarte>
            <MyPaper>
                <DropdownEntiteFormulaire
                    labelCol={LABEL_COL}
                    id="fk_methode_paiement"
                    titre="Methode de paiement"
                    entite={MethodePaiement}
                    champs="methode_paiement"
                    estPetit
                />
                <ChampFormulaire labelCol={LABEL_COL} id="nom_acces_d" titre="Nom dans AccesD" estPetit />
            </MyPaper>
        </div>
    );
}

export default function DetailsFournisseur(): ReactElement {
    const { id } = useParams() as { id: string };
    const listLogs = useGetQuery<HistoriqueLogs[]>(`historique_logs/${id}/fournisseur`);

    return (
        <div>
            <FormulaireModificationEntite
                id={+id}
                entite={Fournisseur}
                badges={(f: Fournisseur) => [
                    `Fournisseur #${f.id}`,
                    `Créé: ${DateTime.fromISO(f.created_at).toRelativeCalendar()}`,
                    `Dernière modification: ${
                        listLogs.data?.[listLogs.data.length - 1]?.date_modification
                            ? dateFormatter(listLogs.data?.[listLogs.data.length - 1]?.date_modification)
                            : 'Aucune modificiation'
                    }  ${
                        listLogs.data?.[listLogs.data.length - 1]?.nom_usager
                            ? ' par ' + listLogs.data?.[listLogs.data.length - 1]?.nom_usager
                            : ''
                    }`,
                ]}
                schema={schemaFournisseur}
                properties_to_reassign={{
                    contact_par_defaut: 'fk_id_contact_par_defaut',
                    contact_gestionnaire: 'fk_id_contact_gestionnaire',
                    lieu: 'fk_id_lieu',
                }}
            >
                <ChampsFournisseur />
            </FormulaireModificationEntite>

            <LogsLoader id={id} tableName={'fournisseur'} />
        </div>
    );
}
